import { useEffect, useState } from "react";
import 'chart.js/auto';
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { FaClipboardList, FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaMoneyBillWave, FaWallet, FaChartBar, FaExclamationCircle } from "react-icons/fa";

import {
    getEmiPaymentPerformance,
    getFetchRevenueTrends,
    getLoanStatusBreakdown,
    getLoanStatistics
} from "../../Api/Api";
import { useWorkshop } from "../Common/WorkshopContext";
import { useNotification } from "../Notification/NotificationContext";
import FaultyLoans from "./FaultyLoans";

const LoanDashboard = () => {
    const { workshopId } = useWorkshop();
    const [loanStatusData, setLoanStatusData] = useState(null);
    const [emiPaymentData, setEmiPaymentData] = useState(null);
    const [revenueData, setRevenueData] = useState(null);
    const [loanStats, setLoanStats] = useState(null);
    const { showNotification } = useNotification();

    useEffect(() => {
        fetchLoanStatusBreakdown();
        fetchEmiPaymentPerformance();
        fetchRevenueTrends();
        fetchLoanStatistics();
    }, []);

    const loanStatsIcons = {
        'Total Loans Issued': <FaClipboardList className="text-teal-500 text-2xl" />,
        'Active Loans': <FaCheckCircle className="text-green-500 text-2xl" />,
        'Closed Loans': <FaTimesCircle className="text-gray-500 text-2xl" />,
        'Overdue Loans': <FaExclamationTriangle className="text-red-500 text-2xl" />,
        'Total Loan Amount': <FaMoneyBillWave className="text-blue-500 text-2xl" />,
        'Principal Recovered': <FaWallet className="text-green-500 text-2xl" />,
        'Interest Earned': <FaChartBar className="text-purple-500 text-2xl" />,
        'Penalties Collected': <FaExclamationCircle className="text-orange-500 text-2xl" />,
        'Outstanding Loan': <FaMoneyBillWave className="text-red-500 text-2xl" />,
        'Total EMIs Paid': <FaCheckCircle className="text-green-500 text-2xl" />,
        'Overdue EMIs': <FaExclamationTriangle className="text-orange-500 text-2xl" />,
        'Revenue from EMIs': <FaChartBar className="text-blue-500 text-2xl" />,
        'Pending EMI Amount': <FaMoneyBillWave className="text-yellow-500 text-2xl" />,
        'Pending Interest': <FaChartBar className="text-purple-500 text-2xl" />,
        'Pending Penalties': <FaExclamationCircle className="text-orange-500 text-2xl" />,
        'Total Pending EMIs': <FaClipboardList className="text-teal-500 text-2xl" />,
        'Loans with Pending EMIs': <FaExclamationTriangle className="text-red-500 text-2xl" />
    };


    const fetchLoanStatusBreakdown = async () => {
        try {
            const response = await getLoanStatusBreakdown(workshopId);
            const labels = Object.keys(response.data);
            const values = Object.values(response.data);

            setLoanStatusData({
                labels,
                datasets: [{
                    data: values,
                    backgroundColor: ["#4CAF50", "#FF9800", "#F44336", "#2196F3"]
                }]
            });
        } catch (error) {
            showNotification(error.message, "error");
        }
    };

    const fetchEmiPaymentPerformance = async () => {
        try {
            const response = await getEmiPaymentPerformance(workshopId);
            const months = response.data.map(d => `${d.month}/${d.year}`);
            const paid = response.data.filter(d => d.status === "PAID").map(d => d.count);
            const overdue = response.data.filter(d => d.status === "OVERDUE").map(d => d.count);

            setEmiPaymentData({
                labels: months,
                datasets: [
                    { label: "Paid EMIs", data: paid, backgroundColor: "#4CAF50" },
                    { label: "Overdue EMIs", data: overdue, backgroundColor: "#F44336" }
                ]
            });
        } catch (error) {
            showNotification(error.message, "error");
        }
    };

    const fetchRevenueTrends = async () => {
        try {
            const response = await getFetchRevenueTrends(workshopId);
            if (!response.data || response.data.length === 0) {
                showNotification("No revenue data available", "warning");
                return;
            }

            let data = response.data;
            if (data.length === 1) {
                data = [...data, { ...data[0], month: data[0].month - 1 }];
            }

            const months = data.map(d => `${d.month}/${d.year}`);
            const revenue = data.map(d => d.revenue);

            setRevenueData({
                labels: months,
                datasets: [{
                    label: "Revenue (₹)",
                    data: revenue,
                    backgroundColor: "#2196F3",
                    borderColor: "#1976D2",
                    fill: false
                }]
            });
        } catch (error) {
            showNotification(error.message, "error");
        }
    };

    const fetchLoanStatistics = async () => {
        try {
            const response = await getLoanStatistics(workshopId);
            setLoanStats(response.data);
        } catch (error) {
            showNotification(error.message, "error");
        }
    };

    return (
        <div className="max-w-screen-xl mx-auto mt-6">
            <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                <div className="bg-teal-500 text-white p-4 rounded-t-lg">
                    <h2 className="text-2xl font-semibold text-center">Loan Management Dashboard</h2>
                </div>

                {/* Loan Statistics Cards */}
                {loanStats && (
                    <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {Object.entries({
                            'Total Loans Issued': loanStats.totalLoansIssued,
                            'Active Loans': loanStats.activeLoans,
                            'Closed Loans': loanStats.closedLoans,
                            // 'Overdue Loans': loanStats.overdueLoans,
                            'Total Loan Amount': `₹${loanStats.totalLoanAmountIssued.toLocaleString()}`,
                            'Principal Recovered': `₹${loanStats.totalPrincipalRecovered.toLocaleString()}`,
                            'Interest Earned': `₹${loanStats.totalInterestEarned.toLocaleString()}`,
                            'Penalties Collected': `₹${loanStats.totalPenaltiesCollected.toLocaleString()}`,
                            'Outstanding Loan': `₹${loanStats.totalOutstandingLoan.toLocaleString()}`,
                            'Total EMIs Paid': loanStats.totalEmisPaid,
                            'Overdue EMIs': loanStats.totalOverdueEmis,
                            'Revenue from EMIs': `₹${loanStats.totalRevenueFromEmis.toLocaleString()}`,
                            'Pending EMI Amount': `₹${loanStats.totalPendingEmiAmount.toLocaleString()}`,
                            'Pending Interest': loanStats.totalPendingInterest ? `₹${loanStats.totalPendingInterest.toLocaleString()}` : 'N/A',
                            'Pending Penalties': `₹${loanStats.totalPendingPenalties.toLocaleString()}`,
                            'Total Pending EMIs': loanStats.totalPendingEmis,
                            'Loans with Pending EMIs': loanStats.totalLoansWithOverdueEmis
                        }).map(([title, value], index) => (
                            <div key={index} className="bg-white shadow-md p-4 rounded-lg flex items-center">
                                {loanStatsIcons[title]}
                                <div className="ml-3">
                                    <p className="text-gray-600 text-sm">{title}</p>
                                    <p className="text-lg font-semibold text-teal-600">{value}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}


                {/* Charts Section */}
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {loanStatusData && (
                        <div className="bg-white p-4 rounded-lg shadow-md">
                            <h3 className="text-sm font-semibold mb-2 text-center">Loan Repayment Status</h3>
                            <Doughnut data={loanStatusData} className="h-48" />
                        </div>
                    )}

                    {emiPaymentData && (
                        <div className="bg-white p-4 rounded-lg shadow-md">
                            <h3 className="text-sm font-semibold mb-2 text-center">EMI Payment Performance</h3>
                            <Bar data={emiPaymentData} className="h-48" />
                        </div>
                    )}

                    {revenueData && (
                        <div className="bg-white p-4 rounded-lg shadow-md">
                            <h3 className="text-sm font-semibold mb-2 text-center">Revenue from Loan Repayments</h3>
                            <Line data={revenueData} className="h-48" />
                        </div>
                    )}
                </div>

                <FaultyLoans/>
            </div>
        </div>
    );
};

export default LoanDashboard;
